// Gespeicherte Cookie-Einwilligung lesen, ohne React und ohne weitere Imports.
//
// Quelle der Wahrheit bleibt localStorage `cc_cookie_consent` (geschrieben von
// useCookieConsent.saveConsent). Eigenes Modul, damit Tracking-Helfer wie
// facebookTracking die Einwilligung prüfen können, ohne useCookieConsent zu
// importieren (das importiert umgekehrt die Helfer → Zirkel).

export const CONSENT_KEY = "cc_cookie_consent";
export const CONSENT_VERSION = "1.0";

export interface GespeicherteEinwilligung {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

/** Gespeicherte Einwilligung oder null (keine Entscheidung / alte Version / kein Zugriff). */
export function gespeicherteEinwilligung(): GespeicherteEinwilligung | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GespeicherteEinwilligung;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** true nur bei ausdrücklicher Marketing-Einwilligung. */
export function hatMarketingEinwilligung(): boolean {
  return gespeicherteEinwilligung()?.marketing === true;
}

/** Kennung des Einwilligungstexts, dem zugestimmt wurde (Cookie-Banner, Version aus CONSENT_VERSION). */
export const MARKETING_EINWILLIGUNG_TEXT_VERSION = `cookie-banner-${CONSENT_VERSION}`;

export interface LeadEinwilligung {
  marketing_consent: boolean;
  marketing_consent_at: string | null;
  marketing_consent_text_version: string | null;
}

/**
 * Einwilligungs-Stand für die Speicherung am Lead (waitlist_signups, seit 16.10.2026).
 * marketing_consent: gleiche Regel wie hatMarketingEinwilligung / getFacebookTrackingData
 * (nur ausdrückliches Ja). marketing_consent_at: Zeitpunkt der Banner-Entscheidung (Ja oder
 * Nein); fehlt oder ist er unlesbar oder liegt in der Zukunft, gilt `jetzt`.
 * Ohne Entscheidung im Banner: false, Zeitpunkt und Textversion leer.
 */
export function einwilligungFuerLead(
  gespeichert: GespeicherteEinwilligung | null = gespeicherteEinwilligung(),
  jetzt: Date = new Date(),
): LeadEinwilligung {
  if (!gespeichert) {
    return { marketing_consent: false, marketing_consent_at: null, marketing_consent_text_version: null };
  }
  const ms = Date.parse(gespeichert.timestamp);
  const zeitpunkt = Number.isFinite(ms) && ms <= jetzt.getTime() ? new Date(ms) : jetzt;
  return {
    marketing_consent: gespeichert.marketing === true,
    marketing_consent_at: zeitpunkt.toISOString(),
    marketing_consent_text_version: MARKETING_EINWILLIGUNG_TEXT_VERSION,
  };
}

/**
 * Einwilligung schreiben, ohne React (Kunden-App: Statistik-Frage im Erststart und im Konto,
 * src/lib/appStatistik.ts). Gleiches Format wie useCookieConsent.saveConsent. Gibt false zurück,
 * wenn localStorage fehlt (privater Modus): dann gilt weiter „nicht entschieden“.
 */
export function speichereEinwilligung(analytics: boolean, marketing: boolean): boolean {
  const full: GespeicherteEinwilligung = {
    necessary: true,
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(full));
    return true;
  } catch {
    return false;
  }
}
